import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';

const CargosManuales = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [zonaFilter, setZonaFilter] = useState('todas');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    filtrarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes, searchTerm, zonaFilter, estadoFilter]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [clientesRes, zonasRes] = await Promise.all([
        axios.get('/usuarios'),
        axios.get('/zonas')
      ]);

      const soloClientes = (clientesRes.data || []).filter(u => u.rol === 'cliente');
      setClientes(soloClientes);
      setZonas(zonasRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar los clientes y zonas.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setLoading(false);
    }
  };

  const filtrarClientes = () => {
    let filtered = [...clientes];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        c =>
          c.nombre?.toLowerCase().includes(term) ||
          c.padron?.toLowerCase().includes(term) ||
          c.cuit?.toLowerCase().includes(term) ||
          c.direccion?.toLowerCase().includes(term)
      );
    }

    if (zonaFilter !== 'todas') {
      filtered = filtered.filter(c => c.zona?.id === parseInt(zonaFilter));
    }

    if (estadoFilter !== 'todos') {
      if (estadoFilter === 'activo') {
        filtered = filtered.filter(c => c.activo !== false);
      } else if (estadoFilter === 'inactivo') {
        filtered = filtered.filter(c => c.activo === false);
      } else if (estadoFilter === 'baja') {
        filtered = filtered.filter(c => c.servicio_dado_de_baja);
      }
    }

    setFilteredClientes(filtered);
    setCurrentPage(1);
  };

  const getEstadoBadge = (cliente) => {
    let estado = 'activo';
    const badgeClasses = {
      activo: 'bg-active-green/10 text-active-green',
      inactivo: 'bg-inactive-gray/10 text-inactive-gray',
      baja: 'bg-red-100 text-red-700',
    };
    const labels = {
      activo: 'Activo',
      inactivo: 'Inactivo',
      baja: 'Baja servicio',
    };

    if (cliente.servicio_dado_de_baja) {
      estado = 'baja';
    } else if (cliente.activo === false) {
      estado = 'inactivo';
    }

    return { class: badgeClasses[estado], label: labels[estado] };
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClientes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <div className="bg-white border-b">
          <div className="px-8 py-6">
            <h1 className="text-2xl font-bold text-gray-900">Cargos Manuales</h1>
            <p className="text-gray-600 mt-1">Seleccione un cliente para ver y crear cargos manuales</p>
          </div>
        </div>

        <div className="p-8 space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <label className="flex flex-col w-full">
                  <div className="flex w-full flex-1 items-stretch rounded-lg h-12 bg-[#f0f3f4]">
                    <div className="text-[#617c89] flex items-center justify-center pl-4">
                      <span className="material-symbols-outlined">search</span>
                    </div>
                    <input
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden text-[#111618] focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-[#617c89] px-2 text-base font-normal leading-normal"
                      placeholder="Buscar por nombre, padrón, CUIT o dirección..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </label>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={zonaFilter}
                  onChange={(e) => setZonaFilter(e.target.value)}
                  className="flex h-12 w-full items-center justify-between gap-x-2 rounded-lg bg-[#f0f3f4] px-4 text-[#111618] border-none focus:outline-none focus:ring-0"
                >
                  <option value="todas">Zona: Todas</option>
                  {zonas.map((zona) => (
                    <option key={zona.id} value={zona.id}>
                      Zona: {zona.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={estadoFilter}
                  onChange={(e) => setEstadoFilter(e.target.value)}
                  className="flex h-12 w-full items-center justify-between gap-x-2 rounded-lg bg-[#f0f3f4] px-4 text-[#111618] border-none focus:outline-none focus:ring-0"
                >
                  <option value="todos">Estado: Todos</option>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="baja">Baja servicio</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Clientes</h3>
              <span className="text-sm text-gray-500">{filteredClientes.length} resultados</span>
            </div>

            {loading ? (
              <div className="p-8 text-center text-gray-500">Cargando clientes...</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-white">
                      <tr className="border-b">
                        <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Padrón</th>
                        <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                        <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Zona</th>
                        <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                        <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {currentItems.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                            No hay clientes para mostrar con estos filtros.
                          </td>
                        </tr>
                      ) : (
                        currentItems.map((cliente) => {
                          const estadoBadge = getEstadoBadge(cliente);
                          return (
                            <tr key={cliente.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{cliente.padron}</td>
                              <td className="px-6 py-4 text-sm text-gray-700">{cliente.nombre}</td>
                              <td className="px-6 py-4 text-sm text-gray-700">{cliente.zona?.nombre || '-'}</td>
                              <td className="px-6 py-4 text-sm">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${estadoBadge.class}`}>
                                  {estadoBadge.label}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <button
                                  onClick={() => navigate(`/administrativo/cargos-manuales/${cliente.id}`)}
                                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                  Ver detalle
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                    <span className="text-sm text-gray-600">
                      Página {currentPage} de {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        className="px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        className="px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                        disabled={currentPage === totalPages}
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
    </div>
  );
};

export default CargosManuales;

